import React, { useState, useEffect } from 'react';
import MarkdownQuillEditor from './MarkdownQuillEditor';

// ─────────────────────────────────────────────────────────────────────────────
// Accordion wrapper
// ─────────────────────────────────────────────────────────────────────────────
const AccordionSection = ({ sectionKey, title, children, toggleSection, expandedSections, defaultExpanded = false }) => {
  const isExpanded = expandedSections?.[sectionKey] ?? defaultExpanded;
  return (
    <div className="border border-gray-200 rounded-lg mb-3 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => toggleSection?.(sectionKey)}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
        aria-expanded={isExpanded}
      >
        <span className="text-base font-semibold text-gray-900">{title}</span>
        <svg className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div className={`overflow-hidden transition-all duration-200 ${isExpanded ? 'max-h-[6000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="p-4 pt-2 space-y-4">{children}</div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Section content editor — renders appropriate input based on content shape
// ─────────────────────────────────────────────────────────────────────────────
const SectionContentEditor = ({ section, onUpdate }) => {
  const { content, key: sectionKey } = section;

  if (!content) return <p className="text-sm text-gray-400 italic">No content.</p>;

  // ── Regional Analysis section or regions array ────────────────────────────
  if (sectionKey === 'regional_analysis' || 'regions' in content) {
    const regions = Array.isArray(content.regions) ? content.regions : [];
    const total = regions.reduce((s, r) => s + (Number(r.sharePercent) || 0), 0);
    const cls = "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
    const setRegion = (idx, patch) => onUpdate({ regions: regions.map((r, i) => i === idx ? { ...r, ...patch } : r) });
    const writeupVal = content.text || (Array.isArray(content.paragraphs) ? content.paragraphs.join('\n\n') : '') || '';

    return (
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Overall Regional Analysis Write-Up</label>
          <p className="text-xs text-gray-500 mb-2">Narrative write-up displayed above the regional breakdown cards on the report page.</p>
          <MarkdownQuillEditor
            value={writeupVal}
            onChange={val => onUpdate({ text: val, paragraphs: [val] })}
            placeholder="Enter overall regional analysis narrative..."
            rows={10}
          />
        </div>

        <div className="border-t border-gray-200 pt-3">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs font-semibold text-gray-700 mb-0">Regional Map Shares Breakdown</label>
            {regions.length === 0 && (
              <button
                type="button"
                onClick={() => onUpdate({
                  regions: [
                    { name: "North America", sharePercent: 35, analysis: "" },
                    { name: "Europe", sharePercent: 25, analysis: "" },
                    { name: "Asia-Pacific", sharePercent: 25, analysis: "" },
                    { name: "Latin America", sharePercent: 8, analysis: "" },
                    { name: "Middle East & Africa", sharePercent: 7, analysis: "" },
                  ]
                })}
                className="px-2.5 py-1 text-xs bg-purple-50 text-purple-600 border border-purple-200 rounded hover:bg-purple-100"
              >
                + Populate Standard 5 Regions
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mb-2">Total share: <strong className={total > 100 ? 'text-red-600' : 'text-green-600'}>{total.toFixed(1)}%</strong></p>
          <div className="space-y-3">
            {regions.map((r, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <div className="grid grid-cols-3 gap-3 mb-2">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Region</label>
                    <input type="text" value={r.name || ''} onChange={e => setRegion(idx, { name: e.target.value })} className={cls} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Share %</label>
                    <input type="number" step="0.1" value={r.sharePercent ?? ''} onChange={e => setRegion(idx, { sharePercent: parseFloat(e.target.value) || 0 })} className={cls} />
                  </div>
                </div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Analysis</label>
                <textarea value={r.analysis || ''} onChange={e => setRegion(idx, { analysis: e.target.value })} rows={3} className={cls} />
                <button type="button" onClick={() => onUpdate({ regions: regions.filter((_, i) => i !== idx) })} className="mt-2 text-red-500 text-xs hover:text-red-700 font-medium">Remove Region</button>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => onUpdate({ regions: [...regions, { name: '', sharePercent: 0, analysis: '' }] })} className="mt-3 px-4 py-2 text-sm bg-blue-50 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-100">+ Add Region</button>
        </div>
      </div>
    );
  }

  // ── Single text block ─────────────────────────────────────────────────────
  if ('text' in content) {
    return (
      <div className="mb-2">
        <MarkdownQuillEditor
          value={content.text || ''}
          onChange={val => onUpdate({ text: val })}
          placeholder="Enter content..."
          rows={14}
        />
      </div>
    );
  }

  // ── Paragraphs array ──────────────────────────────────────────────────────
  if ('paragraphs' in content) {
    const paras = Array.isArray(content.paragraphs) ? content.paragraphs : [];
    return (
      <div className="space-y-3">
        {paras.map((para, idx) => (
          <div key={idx} className="flex gap-2 items-start">
            <span className="text-xs text-gray-400 font-medium pt-3 w-5 text-right flex-shrink-0">{idx + 1}</span>
            <div className="flex-1">
              <MarkdownQuillEditor
                value={para}
                onChange={val => {
                  const updated = [...paras];
                  updated[idx] = val;
                  onUpdate({ paragraphs: updated });
                }}
                placeholder={`Paragraph ${idx + 1}`}
                rows={5}
              />
            </div>
            <button
              type="button"
              onClick={() => onUpdate({ paragraphs: paras.filter((_, i) => i !== idx) })}
              className="mt-2 text-red-400 hover:text-red-600 text-xl font-bold flex-shrink-0"
              title="Remove"
            >×</button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onUpdate({ paragraphs: [...paras, ''] })}
          className="px-4 py-2 text-sm bg-blue-50 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
        >+ Add Paragraph</button>
      </div>
    );
  }

  // ── JSON items ────────────────────────────────────────────────────────────
  if ('items' in content) {
    const items = Array.isArray(content.items) ? content.items : [];
    const firstItem = items[0] || {};
    const isFAQ = 'question' in firstItem || 'answer' in firstItem || sectionKey === 'faqs';

    if (isFAQ) {
      return (
        <div className="space-y-4">
          {items.map((item, idx) => (
            <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50 relative">
              <span className="absolute top-2 right-3 text-xs text-gray-400 font-semibold">Q{idx + 1}</span>
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">Question</label>
                <textarea
                  value={item.question || ''}
                  onChange={e => { const u = items.map((it, i) => i === idx ? { ...it, question: e.target.value } : it); onUpdate({ items: u }); }}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Answer</label>
                <textarea
                  value={item.answer || ''}
                  onChange={e => { const u = items.map((it, i) => i === idx ? { ...it, answer: e.target.value } : it); onUpdate({ items: u }); }}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button type="button" onClick={() => onUpdate({ items: items.filter((_, i) => i !== idx) })} className="mt-3 text-red-500 text-xs hover:text-red-700 font-medium">Remove FAQ</button>
            </div>
          ))}
          <button type="button" onClick={() => onUpdate({ items: [...items, { question: '', answer: '' }] })} className="px-4 py-2 text-sm bg-blue-50 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-100">+ Add FAQ</button>
        </div>
      );
    }

    // Recent developments / month-year-description items
    return (
      <div className="space-y-4">
        {items.map((item, idx) => (
          <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Month</label>
                <input type="text" value={item.month || ''} onChange={e => { const u = items.map((it, i) => i === idx ? { ...it, month: e.target.value } : it); onUpdate({ items: u }); }} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g., January" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
                <input type="text" value={item.year || ''} onChange={e => { const u = items.map((it, i) => i === idx ? { ...it, year: e.target.value } : it); onUpdate({ items: u }); }} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g., 2025" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <textarea value={item.description || ''} onChange={e => { const u = items.map((it, i) => i === idx ? { ...it, description: e.target.value } : it); onUpdate({ items: u }); }} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <button type="button" onClick={() => onUpdate({ items: items.filter((_, i) => i !== idx) })} className="mt-3 text-red-500 text-xs hover:text-red-700 font-medium">Remove</button>
          </div>
        ))}
        <button type="button" onClick={() => onUpdate({ items: [...items, { month: '', year: '', description: '' }] })} className="px-4 py-2 text-sm bg-blue-50 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-100">+ Add Entry</button>
      </div>
    );
  }

  // ── Bullets array ─────────────────────────────────────────────────────────
  if ('bullets' in content) {
    const bullets = Array.isArray(content.bullets) ? content.bullets : [];
    return (
      <div className="space-y-3">
        {bullets.map((b, idx) => (
          <div key={idx} className="flex gap-2 items-center">
            <span className="text-xs text-gray-400 font-medium w-5 text-right flex-shrink-0">{idx + 1}</span>
            <input
              type="text"
              value={b}
              onChange={e => {
                const updated = [...bullets];
                updated[idx] = e.target.value;
                onUpdate({ bullets: updated });
              }}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={`Bullet ${idx + 1}`}
            />
            <button
              type="button"
              onClick={() => onUpdate({ bullets: bullets.filter((_, i) => i !== idx) })}
              className="text-red-400 hover:text-red-600 text-xl font-bold flex-shrink-0"
              title="Remove"
            >×</button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onUpdate({ bullets: [...bullets, ''] })}
          className="px-4 py-2 text-sm bg-blue-50 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
        >+ Add Bullet</button>
      </div>
    );
  }

  // ── Companies array ───────────────────────────────────────────────────────
  if ('companies' in content) {
    const companies = Array.isArray(content.companies) ? content.companies : [];
    return (
      <div className="space-y-4">
        {companies.map((c, idx) => (
          <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50 relative">
            <span className="absolute top-2 right-3 text-xs text-gray-400 font-semibold">Company {idx + 1}</span>
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">Company Name</label>
              <input
                type="text"
                value={c.company || ''}
                onChange={e => {
                  const updated = [...companies];
                  updated[idx] = { ...c, company: e.target.value };
                  onUpdate({ companies: updated });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Summary</label>
              <textarea
                value={c.summary || ''}
                onChange={e => {
                  const updated = [...companies];
                  updated[idx] = { ...c, summary: e.target.value };
                  onUpdate({ companies: updated });
                }}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              type="button"
              onClick={() => onUpdate({ companies: companies.filter((_, i) => i !== idx) })}
              className="mt-3 text-red-500 text-xs hover:text-red-700 font-medium"
            >Remove Company</button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onUpdate({ companies: [...companies, { company: '', summary: '' }] })}
          className="px-4 py-2 text-sm bg-blue-50 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-100"
        >+ Add Company</button>
      </div>
    );
  }

  return <p className="text-xs text-gray-400 italic">Unrecognised content shape: <code>{JSON.stringify(content).substring(0, 80)}…</code></p>;
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
export default function EditReportForm({ report, onChange, errors = {} }) {

  // Build initial accordion state: basicInfo + keyPlayers + keyMetrics + one key per section
  const buildInitialState = () => {
    const state = {
      basicInfo: true,
      keyPlayers: false,
      seoMeta: false,
      keyMetrics: false,
      marketSegments: false,
      charts: false,
    };
    (report.content?.sections || []).forEach(s => { state[s.key] = false; });
    return state;
  };

  const [expandedSections, setExpandedSections] = useState(buildInitialState);
  const toggleSection = key => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  // ── Key Players helpers ───────────────────────────────────────────────────
  const [playersText, setPlayersText] = useState(() =>
    Array.isArray(report.players) ? report.players.join('\n') : ''
  );

  useEffect(() => {
    setPlayersText(Array.isArray(report.players) ? report.players.join('\n') : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report._id]);

  const handlePlayersChange = (e) => {
    const text = e.target.value;
    setPlayersText(text);
    const parsed = text
      .split(/[\n,]/)
      .map(p => p.trim())
      .filter(Boolean);
    onChange({ ...report, players: parsed });
  };

  const removePlayer = (indexToRemove) => {
    const currentList = Array.isArray(report.players) ? report.players : [];
    const nextList = currentList.filter((_, idx) => idx !== indexToRemove);
    setPlayersText(nextList.join('\n'));
    onChange({ ...report, players: nextList });
  };

  const currentPlayers = Array.isArray(report.players)
    ? report.players
    : (playersText ? playersText.split(/[\n,]/).map(p => p.trim()).filter(Boolean) : []);

  // ── Field helpers ─────────────────────────────────────────────────────────
  const set = (field, value) => onChange({ ...report, [field]: value });

  const setSeo = (field, value) => onChange({
    ...report, seoMetadata: { ...(report.seoMetadata || {}), [field]: value }
  });

  const setKM = (field, value) => onChange({
    ...report, keyMetrics: { ...(report.keyMetrics || {}), [field]: value }
  });

  const setKMNested = (parent, child, value) => onChange({
    ...report,
    keyMetrics: {
      ...(report.keyMetrics || {}),
      [parent]: { ...(report.keyMetrics?.[parent] || {}), [child]: value }
    }
  });

  // Update a specific section's content
  const updateSection = (sectionKey, updatedContent) => {
    const updatedSections = (report.content?.sections || []).map(s =>
      s.key === sectionKey
        ? { ...s, content: { ...s.content, ...updatedContent } }
        : s
    );
    onChange({ ...report, content: { ...report.content, sections: updatedSections } });
  };

  // Update a specific section's metadata
  const updateSectionMeta = (sectionKey, metaUpdate) => {
    const updatedSections = (report.content?.sections || []).map(s =>
      s.key === sectionKey
        ? { ...s, ...metaUpdate }
        : s
    );
    onChange({ ...report, content: { ...report.content, sections: updatedSections } });
  };

  const addSection = () => {
    const currentSections = report.content?.sections || [];
    const newSection = {
      key: `custom_section_${Date.now()}`,
      heading: 'New Section',
      contentType: 'text',
      sequence: currentSections.length > 0 ? Math.max(...currentSections.map(s => s.sequence || 0)) + 1 : 1,
      content: { text: '' }
    };
    const updatedSections = [...currentSections, newSection];
    onChange({ ...report, content: { ...report.content, sections: updatedSections } });
    setExpandedSections(prev => ({ ...prev, [newSection.key]: true }));
  };

  const addRegionalSection = () => {
    const currentSections = report.content?.sections || [];
    const newSection = {
      key: 'regional_analysis',
      heading: 'Regional Analysis',
      contentType: 'json',
      sequence: currentSections.length > 0 ? Math.max(...currentSections.map(s => s.sequence || 0)) + 1 : 1,
      content: { text: '', regions: currentRegions }
    };
    const updatedSections = [...currentSections, newSection];
    onChange({ ...report, content: { ...report.content, sections: updatedSections } });
    setExpandedSections(prev => ({ ...prev, regional_analysis: true }));
  };

  const inputCls = "w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
  const labelCls = "block text-sm font-medium text-gray-700 mb-1";
  const subLabelCls = "block text-xs font-medium text-gray-600 mb-1";

  const sections = report.content?.sections || [];

  // Charts (segment share pies etc.) — stored in report.charts as { name, type, labels[], series[] }
  const charts = Array.isArray(report.charts) ? report.charts : [];
  const setCharts = (next) => onChange({ ...report, charts: next });
  const updateChart = (ci, patch) => setCharts(charts.map((c, i) => (i === ci ? { ...c, ...patch } : c)));

  // ── Regional shares for map (used when adding regional section) ──────────
  const regionalSectionIndex = (report.content?.sections || []).findIndex(
    s => s.key === 'regional_analysis' || (Array.isArray(s.content?.regions) && s.content.regions.length > 0)
  );
  const foundRegionalSection = regionalSectionIndex >= 0 ? report.content?.sections[regionalSectionIndex] : null;
  const foundRegions = foundRegionalSection?.content?.regions;

  const getInitialRegions = () => {
    if (Array.isArray(foundRegions) && foundRegions.length > 0) {
      return foundRegions;
    }
    const dom = report.keyMetrics?.dominantRegion;
    const fast = report.keyMetrics?.fastestGrowingRegion;
    const domShare = dom?.sharePercent || 35;
    const fastShare = fast?.cagrPercent ? Math.min(25, Math.round(fast.cagrPercent * 2.5)) : 20;
    const leftover = Math.max(0, 100 - domShare - fastShare);
    const namedRegions = [dom?.region, fast?.region].filter(Boolean).map(r => r.toLowerCase());
    const allFive = [
      { name: "North America", base: 32 },
      { name: "Europe", base: 25 },
      { name: "Asia-Pacific", base: 28 },
      { name: "Latin America", base: 8 },
      { name: "Middle East & Africa", base: 7 },
    ];
    const unnamed = allFive.filter(r => !namedRegions.some(n => r.name.toLowerCase().includes(n) || n.includes(r.name.toLowerCase())));
    const unnamedTotalBase = unnamed.reduce((s, r) => s + r.base, 0) || 1;
    return allFive.map(r => {
      if (dom?.region && r.name.toLowerCase().includes(dom.region.toLowerCase())) {
        return { name: r.name, sharePercent: domShare, analysis: dom.formatted || "" };
      }
      if (fast?.region && r.name.toLowerCase().includes(fast.region.toLowerCase())) {
        return { name: r.name, sharePercent: fastShare, analysis: fast.formatted || "" };
      }
      return { name: r.name, sharePercent: Math.round((r.base / unnamedTotalBase) * leftover), analysis: "" };
    });
  };

  const currentRegions = getInitialRegions();

  // ── Market Segments (structuredSegments) ──────────────────────────────────
  const structuredSegments = (report.structuredSegments && typeof report.structuredSegments === 'object' && Object.keys(report.structuredSegments).length > 0)
    ? report.structuredSegments
    : (report.content?.structuredSegments && typeof report.content.structuredSegments === 'object')
      ? report.content.structuredSegments
      : (report.structuredSegments && typeof report.structuredSegments === 'object')
        ? report.structuredSegments
        : {};
  const segmentCategories = Object.keys(structuredSegments);

  const updateStructuredSegments = (newObj) => {
    onChange({ ...report, structuredSegments: newObj });
  };

  const handleSegmentCategoryChange = (oldCat, newCat) => {
    const next = {};
    Object.entries(structuredSegments).forEach(([k, v]) => {
      if (k === oldCat) {
        next[newCat] = v;
      } else {
        next[k] = v;
      }
    });
    updateStructuredSegments(next);
  };

  const handleSegmentValuesChange = (cat, valuesStr) => {
    const valuesArr = valuesStr.split(/[\n,]/).map(v => v.trim()).filter(Boolean);
    const next = { ...structuredSegments, [cat]: valuesArr };
    updateStructuredSegments(next);
  };

  const addSegmentCategory = () => {
    let newCatName = "By Category";
    let counter = 1;
    while (structuredSegments[newCatName]) {
      newCatName = `By Category ${counter++}`;
    }
    updateStructuredSegments({ ...structuredSegments, [newCatName]: ["Segment A", "Segment B"] });
  };

  const removeSegmentCategory = (cat) => {
    const next = { ...structuredSegments };
    delete next[cat];
    updateStructuredSegments(next);
  };

  // ── Segment Share Pie Chart ───────────────────────────────────────────────
  const pieChartIndex = charts.findIndex(c => c?.type === 'pie' || /segment share/i.test(c?.name || ''));
  const pieChart = pieChartIndex >= 0 ? charts[pieChartIndex] : null;

  const [selectedPieCategory, setSelectedPieCategory] = useState(() => {
    if (pieChart?.meta?.segmentType && structuredSegments[pieChart.meta.segmentType]) {
      return pieChart.meta.segmentType;
    }
    const found = segmentCategories.find(cat => pieChart?.name?.toLowerCase().includes(cat.toLowerCase()));
    if (found) return found;
    return segmentCategories[0] || "";
  });

  const activePieCategory = (segmentCategories.includes(selectedPieCategory) ? selectedPieCategory : segmentCategories[0]) || "";
  const activeSubsegments = activePieCategory ? (structuredSegments[activePieCategory] || []) : [];

  const getSubsegmentShare = (subName) => {
    if (!pieChart || !Array.isArray(pieChart.labels) || !Array.isArray(pieChart.series)) return 0;
    const idx = pieChart.labels.findIndex(l => l?.trim().toLowerCase() === subName.trim().toLowerCase());
    return idx >= 0 ? (Number(pieChart.series[idx]) || 0) : 0;
  };

  const activeShares = activeSubsegments.map(sub => ({
    name: sub,
    sharePercent: getSubsegmentShare(sub)
  }));
  const activeSharesTotal = activeShares.reduce((s, item) => s + (Number(item.sharePercent) || 0), 0);

  const saveSubsegmentShare = (subName, newPercent) => {
    const numVal = parseFloat(newPercent) || 0;
    const currentLabels = [...activeSubsegments];
    const currentSeries = currentLabels.map(sub => sub === subName ? numVal : getSubsegmentShare(sub));

    const updatedPieChart = {
      name: `Segment Share ${activePieCategory}`,
      type: 'pie',
      labels: currentLabels,
      series: currentSeries,
      meta: { unit: '%', totalValue: null, segmentType: activePieCategory }
    };

    let nextCharts = [...charts];
    if (pieChartIndex >= 0) {
      nextCharts[pieChartIndex] = updatedPieChart;
    } else {
      nextCharts.push(updatedPieChart);
    }
    onChange({ ...report, charts: nextCharts });
  };

  const otherCharts = charts.filter((_, i) => i !== pieChartIndex);

  return (
    <div className="space-y-3">

      {/* ── Basic Information ─────────────────────────────────────────────── */}
      <AccordionSection expandedSections={expandedSections} toggleSection={toggleSection} sectionKey="basicInfo" title="Basic Information" defaultExpanded>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className={labelCls}>Title <span className="text-red-500">*</span></label>
            <input type="text" value={report.title || ''} onChange={e => set('title', e.target.value)} className={`${inputCls} ${errors.title ? 'border-red-500' : ''}`} placeholder="Report title" />
            {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title}</p>}
          </div>
          <div>
            <label className={labelCls}>Market Name</label>
            <input type="text" value={report.marketName || ''} onChange={e => set('marketName', e.target.value)} className={inputCls} placeholder="e.g., Japan 3D Printing Market" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Industry <span className="text-red-500">*</span></label>
              <input type="text" value={report.industry || ''} onChange={e => set('industry', e.target.value)} className={`${inputCls} ${errors.industry ? 'border-red-500' : ''}`} placeholder="Industry name" />
              {errors.industry && <p className="mt-1 text-xs text-red-600">{errors.industry}</p>}
            </div>
            <div>
              <label className={labelCls}>Template Key</label>
              <input type="text" value={report.templateKey || ''} disabled className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-100 text-gray-500 text-sm cursor-not-allowed" />
            </div>
          </div>
          {report.model && (
            <div>
              <label className={labelCls}>AI Model</label>
              <input type="text" value={`${report.vendor || ''} / ${report.model || ''}`} disabled className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-100 text-gray-500 text-sm cursor-not-allowed" />
            </div>
          )}
        </div>
      </AccordionSection>

      {/* ── Key Players / Companies ───────────────────────────────────────── */}
      <AccordionSection expandedSections={expandedSections} toggleSection={toggleSection} sectionKey="keyPlayers" title="Key Players / Companies">
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={labelCls}>
                Key Players List <span className="text-gray-500 font-normal">(one player per line or comma-separated)</span>
              </label>
              <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">
                {currentPlayers.length} {currentPlayers.length === 1 ? 'Player' : 'Players'}
              </span>
            </div>
            <textarea
              rows={6}
              value={playersText}
              onChange={handlePlayersChange}
              className={`${inputCls} font-mono text-xs`}
              placeholder={"Company A\nCompany B\nCompany C\nor Company A, Company B, Company C"}
            />
            <p className="mt-1 text-xs text-gray-500">
              Enter key market players. You can enter them one per line or separated with commas. These players appear on the public report page under Key Players and Quick Stats.
            </p>
          </div>

          {currentPlayers.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-2">Detected Players ({currentPlayers.length}):</p>
              <div className="flex flex-wrap gap-1.5 p-3 bg-gray-50 rounded-lg border border-gray-200 max-h-48 overflow-y-auto">
                {currentPlayers.map((player, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-white text-gray-800 border border-gray-300 shadow-sm"
                  >
                    <span>{player}</span>
                    <button
                      type="button"
                      onClick={() => removePlayer(idx)}
                      className="text-gray-400 hover:text-red-600 font-bold focus:outline-none ml-0.5"
                      title={`Remove ${player}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </AccordionSection>

      {/* ── SEO Metadata ──────────────────────────────────────────────────── */}
      <AccordionSection expandedSections={expandedSections} toggleSection={toggleSection} sectionKey="seoMeta" title="SEO Metadata">
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Meta Title</label>
            <input type="text" value={report.seoMetadata?.metaTitle || ''} onChange={e => setSeo('metaTitle', e.target.value)} className={inputCls} placeholder="Meta title" />
            <p className="mt-1 text-xs text-gray-400">{(report.seoMetadata?.metaTitle || '').length} characters (recommended ≤ 60)</p>
          </div>
          <div>
            <label className={labelCls}>Meta Description</label>
            <textarea rows={3} value={report.seoMetadata?.metaDescription || ''} onChange={e => setSeo('metaDescription', e.target.value)} className={inputCls} placeholder="Meta description" />
            <p className="mt-1 text-xs text-gray-400">{(report.seoMetadata?.metaDescription || '').length} characters (recommended ≤ 160)</p>
          </div>
        </div>
      </AccordionSection>

      {/* ── Key Metrics ───────────────────────────────────────────────────── */}
      <AccordionSection expandedSections={expandedSections} toggleSection={toggleSection} sectionKey="keyMetrics" title="Key Metrics">
        <div className="space-y-5">
          {/* Base / Forecast year */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={subLabelCls}>Base Year</label>
              <input type="number" value={report.keyMetrics?.baseYear || ''} onChange={e => setKM('baseYear', parseInt(e.target.value) || 0)} className={inputCls} placeholder="e.g., 2025" />
            </div>
            <div>
              <label className={subLabelCls}>Forecast Year Start</label>
              <input type="number" value={report.keyMetrics?.forecastYearStart || ''} onChange={e => setKM('forecastYearStart', parseInt(e.target.value) || 0)} className={inputCls} placeholder="e.g., 2026" />
            </div>
            <div>
              <label className={subLabelCls}>Forecast Year End</label>
              <input type="number" value={report.keyMetrics?.forecastYear || ''} onChange={e => setKM('forecastYear', parseInt(e.target.value) || 0)} className={inputCls} placeholder="e.g., 2035" />
            </div>
          </div>
          <div>
            <label className={subLabelCls}>Forecast Period</label>
            <input type="text" value={report.keyMetrics?.forecastPeriod || ''} onChange={e => setKM('forecastPeriod', e.target.value)} className={inputCls} placeholder="e.g., 2026-2035" />
          </div>

          {/* Market Size */}
          <div className="border-t pt-4">
            <p className="text-sm font-semibold text-gray-800 mb-3">Market Size (current year)</p>
            <div className="grid grid-cols-3 gap-3">
              <div><label className={subLabelCls}>Value (number)</label><input type="number" step="0.01" value={report.keyMetrics?.marketSize?.value || ''} onChange={e => setKMNested('marketSize', 'value', parseFloat(e.target.value) || 0)} className={inputCls} placeholder="e.g., 0.95" /></div>
              <div><label className={subLabelCls}>Unit</label><input type="text" value={report.keyMetrics?.marketSize?.unit || ''} onChange={e => setKMNested('marketSize', 'unit', e.target.value)} className={inputCls} placeholder="e.g., Billion" /></div>
              <div><label className={subLabelCls}>Formatted</label><input type="text" value={report.keyMetrics?.marketSize?.formatted || ''} onChange={e => setKMNested('marketSize', 'formatted', e.target.value)} className={inputCls} placeholder="e.g., USD 0.9 Billion" /></div>
            </div>
          </div>

          {/* Projected Market Size */}
          <div className="border-t pt-4">
            <p className="text-sm font-semibold text-gray-800 mb-3">Projected Market Size (forecast year)</p>
            <div className="grid grid-cols-3 gap-3">
              <div><label className={subLabelCls}>Value (number)</label><input type="number" step="0.01" value={report.keyMetrics?.projectedMarketSize?.value || ''} onChange={e => setKMNested('projectedMarketSize', 'value', parseFloat(e.target.value) || 0)} className={inputCls} placeholder="e.g., 3.25" /></div>
              <div><label className={subLabelCls}>Unit</label><input type="text" value={report.keyMetrics?.projectedMarketSize?.unit || ''} onChange={e => setKMNested('projectedMarketSize', 'unit', e.target.value)} className={inputCls} placeholder="e.g., Billion" /></div>
              <div><label className={subLabelCls}>Formatted</label><input type="text" value={report.keyMetrics?.projectedMarketSize?.formatted || ''} onChange={e => setKMNested('projectedMarketSize', 'formatted', e.target.value)} className={inputCls} placeholder="e.g., USD 3.3 Billion" /></div>
            </div>
          </div>

          {/* CAGR */}
          <div className="border-t pt-4">
            <p className="text-sm font-semibold text-gray-800 mb-3">CAGR</p>
            <div className="grid grid-cols-3 gap-3">
              <div><label className={subLabelCls}>Value (%)</label><input type="number" step="0.1" value={report.keyMetrics?.cagr?.value || ''} onChange={e => setKMNested('cagr', 'value', parseFloat(e.target.value) || 0)} className={inputCls} placeholder="e.g., 13.1" /></div>
              <div><label className={subLabelCls}>Formatted</label><input type="text" value={report.keyMetrics?.cagr?.formatted || ''} onChange={e => setKMNested('cagr', 'formatted', e.target.value)} className={inputCls} placeholder="e.g., 13.1%" /></div>
              <div><label className={subLabelCls}>Period</label><input type="text" value={report.keyMetrics?.cagr?.period || ''} onChange={e => setKMNested('cagr', 'period', e.target.value)} className={inputCls} placeholder="e.g., 2026-2035" /></div>
            </div>
          </div>

          {/* Dominant Region */}
          <div className="border-t pt-4">
            <p className="text-sm font-semibold text-gray-800 mb-3">Dominant Region</p>
            <div className="grid grid-cols-3 gap-3">
              <div><label className={subLabelCls}>Region</label><input type="text" value={report.keyMetrics?.dominantRegion?.region || ''} onChange={e => setKMNested('dominantRegion', 'region', e.target.value)} className={inputCls} placeholder="e.g., Kanto" /></div>
              <div><label className={subLabelCls}>Share %</label><input type="number" step="0.1" value={report.keyMetrics?.dominantRegion?.sharePercent || ''} onChange={e => setKMNested('dominantRegion', 'sharePercent', parseFloat(e.target.value) || 0)} className={inputCls} placeholder="e.g., 42.5" /></div>
              <div><label className={subLabelCls}>Formatted</label><input type="text" value={report.keyMetrics?.dominantRegion?.formatted || ''} onChange={e => setKMNested('dominantRegion', 'formatted', e.target.value)} className={inputCls} placeholder="e.g., Kanto (42.5% Share)" /></div>
            </div>
          </div>

          {/* Fastest Growing Region */}
          <div className="border-t pt-4">
            <p className="text-sm font-semibold text-gray-800 mb-3">Fastest Growing Region</p>
            <div className="grid grid-cols-3 gap-3">
              <div><label className={subLabelCls}>Region</label><input type="text" value={report.keyMetrics?.fastestGrowingRegion?.region || ''} onChange={e => setKMNested('fastestGrowingRegion', 'region', e.target.value)} className={inputCls} placeholder="e.g., Kansai" /></div>
              <div><label className={subLabelCls}>CAGR %</label><input type="number" step="0.1" value={report.keyMetrics?.fastestGrowingRegion?.cagrPercent || ''} onChange={e => setKMNested('fastestGrowingRegion', 'cagrPercent', parseFloat(e.target.value) || 0)} className={inputCls} placeholder="e.g., 15.8" /></div>
              <div><label className={subLabelCls}>Formatted</label><input type="text" value={report.keyMetrics?.fastestGrowingRegion?.formatted || ''} onChange={e => setKMNested('fastestGrowingRegion', 'formatted', e.target.value)} className={inputCls} placeholder="e.g., Kansai (15.8% CAGR)" /></div>
            </div>
          </div>

          {/* Leading Segment */}
          <div className="border-t pt-4">
            <p className="text-sm font-semibold text-gray-800 mb-3">Leading Segment</p>
            <div className="grid grid-cols-3 gap-3">
              <div><label className={subLabelCls}>Name</label><input type="text" value={report.keyMetrics?.leadingSegment?.name || ''} onChange={e => setKMNested('leadingSegment', 'name', e.target.value)} className={inputCls} placeholder="e.g., By Material" /></div>
              <div><label className={subLabelCls}>Share %</label><input type="number" step="0.1" value={report.keyMetrics?.leadingSegment?.sharePercent || ''} onChange={e => setKMNested('leadingSegment', 'sharePercent', parseFloat(e.target.value) || 0)} className={inputCls} placeholder="e.g., 38.4" /></div>
              <div><label className={subLabelCls}>Formatted</label><input type="text" value={report.keyMetrics?.leadingSegment?.formatted || ''} onChange={e => setKMNested('leadingSegment', 'formatted', e.target.value)} className={inputCls} placeholder="e.g., By Material (38.4% Share)" /></div>
            </div>
          </div>
        </div>
      </AccordionSection>

      {/* ── Market Segments & Segment Share Graph ─────────────────────────── */}
      <AccordionSection expandedSections={expandedSections} toggleSection={toggleSection} sectionKey="marketSegments" title="Market Segments & Segment Share Graph">
        <div className="space-y-6">
          {/* Part A: Structured Segments */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-800">Part A: Market Segments Breakdown</h4>
              <button
                type="button"
                onClick={addSegmentCategory}
                className="px-2.5 py-1 text-xs bg-blue-50 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-100"
              >
                + Add Segment Category
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Define the segment categories and their subsegments (comma or newline separated).
            </p>

            {segmentCategories.length === 0 ? (
              <p className="text-xs text-gray-400 italic">No segment categories defined yet.</p>
            ) : (
              <div className="space-y-3">
                {segmentCategories.map((cat, ci) => {
                  const values = structuredSegments[cat] || [];
                  const valStr = Array.isArray(values) ? values.join("\n") : String(values);
                  return (
                    <div key={ci} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <div className="flex gap-2 items-start">
                        <div className="w-1/3">
                          <label className={subLabelCls}>Category Name</label>
                          <input
                            type="text"
                            value={cat}
                            onChange={(e) => handleSegmentCategoryChange(cat, e.target.value)}
                            className={inputCls}
                            placeholder="e.g. By Type"
                          />
                        </div>
                        <div className="flex-1">
                          <label className={subLabelCls}>Subsegments (one per line or comma-separated)</label>
                          <textarea
                            rows={3}
                            value={valStr}
                            onChange={(e) => handleSegmentValuesChange(cat, e.target.value)}
                            className={inputCls}
                            placeholder={"Subsegment A\nSubsegment B\nSubsegment C"}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeSegmentCategory(cat)}
                          className="mt-6 text-red-400 hover:text-red-600 text-xl font-bold flex-shrink-0"
                          title="Remove Category"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Part B: Segment Share Graph */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-800 mb-1">Part B: Segment Share Graph (Pie Chart)</h4>
            <p className="text-xs text-gray-500 mb-3">
              Select one segment category from above and configure the percentage share for each subsegment.
            </p>

            {segmentCategories.length === 0 ? (
              <p className="text-xs text-gray-400 italic">Add segment categories in Part A above first.</p>
            ) : (
              <div>
                <div className="mb-3 max-w-sm">
                  <label className={subLabelCls}>Select Segment Category for Graph</label>
                  <select
                    value={activePieCategory}
                    onChange={(e) => {
                      const nextCat = e.target.value;
                      setSelectedPieCategory(nextCat);
                      const subsegs = structuredSegments[nextCat] || [];
                      const updatedPie = {
                        name: `Segment Share ${nextCat}`,
                        type: "pie",
                        labels: subsegs,
                        series: subsegs.map((sub) => getSubsegmentShare(sub)),
                        meta: { unit: "%", totalValue: null, segmentType: nextCat },
                      };
                      let nextCharts = [...charts];
                      if (pieChartIndex >= 0) {
                        nextCharts[pieChartIndex] = updatedPie;
                      } else {
                        nextCharts.push(updatedPie);
                      }
                      onChange({ ...report, charts: nextCharts });
                    }}
                    className={inputCls}
                  >
                    <option value="">-- Choose Category --</option>
                    {segmentCategories.map((c, i) => (
                      <option key={i} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                {activePieCategory && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2">
                      Total share:{" "}
                      <strong className={Math.abs(activeSharesTotal - 100) > 0.5 ? "text-red-600 font-bold" : "text-green-600 font-bold"}>
                        {activeSharesTotal.toFixed(1)}%
                      </strong>{" "}
                      {Math.abs(activeSharesTotal - 100) > 0.5 && "(should equal 100%)"}
                    </p>

                    {activeSubsegments.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">No subsegments found in "{activePieCategory}". Add values above in Part A.</p>
                    ) : (
                      <div className="space-y-2">
                        {activeShares.map((item, idx) => (
                          <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-gray-50 p-2 rounded border border-gray-200">
                            <span className="col-span-8 text-sm font-medium text-gray-700 truncate" title={item.name}>
                              {item.name}
                            </span>
                            <div className="col-span-4 flex items-center gap-1">
                              <input
                                type="number"
                                step="0.1"
                                value={item.sharePercent || ""}
                                onChange={(e) => saveSubsegmentShare(item.name, e.target.value)}
                                className={inputCls}
                                placeholder="%"
                              />
                              <span className="text-xs text-gray-500">%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </AccordionSection>

      {/* ── Market Size & Other Charts ────────────────────────────────────── */}
      {otherCharts.length > 0 && (
        <AccordionSection expandedSections={expandedSections} toggleSection={toggleSection} sectionKey="charts" title="Market Size & Other Charts">
          <div className="space-y-5">
            {otherCharts.map((chart, oi) => {
              const ci = charts.indexOf(chart);
              const labels = Array.isArray(chart.labels) ? chart.labels : [];
              const series = Array.isArray(chart.series) ? chart.series : [];
              const setRow = (ri, lbl, val) => updateChart(ci, {
                labels: labels.map((l, i) => (i === ri ? lbl : l)),
                series: labels.map((_, i) => (i === ri ? val : (series[i] ?? 0))),
              });
              return (
                <div key={oi} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="mb-3">
                    <label className={subLabelCls}>Chart Name {chart.type ? `(${chart.type})` : ""}</label>
                    <input type="text" value={chart.name || ""} onChange={(e) => updateChart(ci, { name: e.target.value })} className={inputCls} />
                  </div>
                  <div className="space-y-2">
                    {labels.map((lbl, ri) => (
                      <div key={ri} className="grid grid-cols-12 gap-2 items-center">
                        <input type="text" value={lbl} onChange={(e) => setRow(ri, e.target.value, series[ri] ?? 0)} className={`${inputCls} col-span-7`} placeholder="Year / Label" />
                        <input type="number" step="0.01" value={series[ri] ?? ""} onChange={(e) => setRow(ri, lbl, parseFloat(e.target.value) || 0)} className={`${inputCls} col-span-4`} placeholder="Value" />
                        <button type="button" onClick={() => updateChart(ci, { labels: labels.filter((_, i) => i !== ri), series: series.filter((_, i) => i !== ri) })} className="text-red-400 hover:text-red-600 text-xl font-bold col-span-1">×</button>
                      </div>
                    ))}
                  </div>
                  <button type="button" onClick={() => updateChart(ci, { labels: [...labels, ""], series: [...series, 0] })} className="mt-3 px-3 py-1.5 text-xs bg-blue-50 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-100">+ Add Row</button>
                </div>
              );
            })}
          </div>
        </AccordionSection>
      )}

      {/* ── Content Sections (dynamic) ────────────────────────────────────── */}
      {sections.length === 0 && (
        <div className="text-center py-10 text-gray-400 border border-dashed border-gray-200 rounded-lg">
          No content sections found in this report.
        </div>
      )}

      {sections.map(section => (
        <AccordionSection
          key={section.key}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
          sectionKey={section.key}
          title={`${section.sequence ? `${section.sequence}. ` : ''}${section.heading}`}
        >
          <div className="grid grid-cols-2 gap-4 mb-4 pb-4 border-b border-gray-100">
            <div>
              <label className={subLabelCls}>Section Heading</label>
              <input 
                type="text" 
                value={section.heading || ''} 
                onChange={e => updateSectionMeta(section.key, { heading: e.target.value })} 
                className={inputCls} 
              />
            </div>
            <div>
              <label className={subLabelCls}>Sequence</label>
              <input 
                type="number" 
                value={section.sequence || ''} 
                onChange={e => updateSectionMeta(section.key, { sequence: parseInt(e.target.value) || 0 })} 
                className={inputCls} 
              />
            </div>
          </div>
          <p className="text-xs text-gray-400 mb-3 font-mono">
            key: <span className="text-blue-500">{section.key}</span>
            {' · '}type: <span className="text-purple-500">{section.contentType}</span>
          </p>
          <SectionContentEditor
            section={section}
            onUpdate={updatedContent => updateSection(section.key, updatedContent)}
          />
        </AccordionSection>
      ))}

      <div className="pt-4 pb-2 flex justify-center gap-3 border-t border-gray-200 mt-4">
        {!sections.some(s => s.key === 'regional_analysis') && (
          <button
            type="button"
            onClick={addRegionalSection}
            className="px-4 py-2 text-sm font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 transition-colors shadow-sm"
          >
            + Add Regional Shares (Map)
          </button>
        )}
        <button
          type="button"
          onClick={addSection}
          className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors shadow-sm"
        >
          + Add New Chapter
        </button>
      </div>

    </div>
  );
}
